import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Play } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { NAV_PRODUCTS } from "../pages/products";

const linkCls = ({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${isActive ? "text-foreground bg-muted" : "text-foreground/70 hover:text-foreground hover:bg-muted"}`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled || open ? "glass border-b border-border/70 soft-shadow" : "bg-transparent"}`} data-testid="navbar">
      <nav className="container-xl flex h-16 items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="nav-logo">
          <img src="/qora-logo.png" alt="Qora" className="h-8 w-auto group-hover:scale-[1.03] transition-transform" />
        </Link>

        <div className="hidden lg:flex items-center gap-0.5">
          {NAV_PRODUCTS.map((p) => (
            <NavLink key={p.key} to={p.path} className={linkCls} data-testid={`nav-${p.key}`}>
              <span className="inline-flex items-center gap-1.5"><p.Icon className="h-4 w-4" style={{ color: p.accent }} /> {p.name.replace("Qora ", "")}{p.badge === "Nuovo" && <span className="ml-0.5 rounded bg-[#dff3ec] px-1 text-[9px] font-extrabold uppercase text-[#0c2d34]">Nuovo</span>}</span>
            </NavLink>
          ))}
          <a href="/#demo" className="px-3 py-2 rounded-lg text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-muted inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="nav-demo"><Play className="h-3.5 w-3.5 fill-current" /> Demo</a>
          <NavLink to="/prezzi" className={({ isActive }) => `px-3.5 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${isActive ? "bg-[#0c2d34] text-white" : "text-[#0c2d34] bg-[#dff3ec] hover:bg-[#c9ebe0]"}`} data-testid="nav-pricing">Prezzi</NavLink>
        </div>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <Button className="btn-grad text-white font-semibold shadow-md" onClick={() => navigate("/app")} data-testid="nav-dashboard">Vai alla dashboard</Button>
          ) : (
            <>
              <Button variant="ghost" className="font-medium" onClick={() => navigate("/login")} data-testid="nav-login">Accedi</Button>
              <Button className="btn-grad text-white font-semibold shadow-md hover:opacity-95" onClick={() => navigate("/registrati")} data-testid="nav-try">Inizia gratis</Button>
            </>
          )}
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <Button size="sm" variant="outline" className="font-bold h-9 bg-white" onClick={() => navigate("/prezzi")} data-testid="nav-pricing-mobile">Prezzi</Button>
          {!user && <Button size="sm" className="btn-grad text-white font-semibold h-9" onClick={() => navigate("/registrati")} data-testid="nav-try-mobile">Inizia gratis</Button>}
          <button className="p-2 rounded-lg hover:bg-muted" onClick={() => setOpen(!open)} aria-label="menu" data-testid="nav-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-border px-5 py-4 flex flex-col gap-1 fade-up bg-white/95 max-h-[80vh] overflow-y-auto" data-testid="nav-mobile">
          <p className="px-3 pt-1 pb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">La suite Qora</p>
          {NAV_PRODUCTS.map((p) => (
            <button key={p.key} onClick={() => go(p.path)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold hover:bg-muted text-left" data-testid={`nav-mobile-${p.key}`}>
              <span className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: p.soft, color: p.accent }}><p.Icon className="h-4 w-4" /></span>
              <span>{p.name}{p.badge && <span className="ml-2 rounded bg-[#dff3ec] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#0c2d34]">{p.badge}</span>}<span className="block text-xs font-normal text-muted-foreground">{p.short}</span></span>
            </button>
          ))}
          <div className="grid grid-cols-3 gap-1 mt-1">
            <button onClick={() => go("/prezzi")} className="px-3 py-3 rounded-lg text-sm font-bold bg-[#dff3ec] text-[#0c2d34] text-center" data-testid="nav-mobile-pricing">Prezzi</button>
            <a href="/#demo" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-sm font-medium hover:bg-muted text-center">Demo</a>
            <a href="/#faq" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-sm font-medium hover:bg-muted text-center">FAQ</a>
          </div>
          {user ? <Button className="btn-grad text-white font-semibold mt-2" onClick={() => go("/app")}>Vai alla dashboard</Button> : (
            <>
              <Button variant="outline" className="font-semibold mt-2" onClick={() => go("/login")}>Accedi</Button>
              <Button className="btn-grad text-white font-semibold mt-1" onClick={() => go("/registrati")}>Inizia gratis</Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
